import React, { useEffect, useState } from "react";
import HelmetWrapper from "../../HelmetWrapper";
import { Col, Container, Row } from "react-bootstrap";
import PageHeadingNew from "../common-components/custom-comps/PageHeadingNew";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import TableSkeleton from "../common-components/TableSkeleton";
import { formatDateTime } from "../common-data/Converts";
import { useGetFetchNew } from "../../data/Queries";
import FilterContainer from "../common-components/filters/filter-container";
import DateRangerFilter from "../common-components/filters/date-ranger-filter";
import { CallIcon, EmailIcon } from "../../images/icons/icons";
import "../../styles/lead-mangement/academy-leads.css";

const AmplifyLanding = ({ userInfoData }) => {
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [brandNameFilter, setBrandNameFilter] = useState("");

  // Build filters object
  const buildFilters = () => {
    const filters = {
      page: isPage + 1,
      limit: pageLimit,
    };

    // Add brand name filter if provided
    if (brandNameFilter.trim()) {
      filters.brand_name = brandNameFilter.trim();
    }

    // Add search term if provided (searches contact_person_name)
    if (searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }

    // Add date range filters if provided
    if (startDate) {
      filters.from_date = startDate;
    }
    if (endDate) {
      filters.to_date = endDate;
    }

    return filters;
  };

  // Fetch fitness coach leads using useGetFetchNew hook
  const {
    data: amplifyLeads,
    isLoading: amplifyLeadsLoading,
    refetch,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "amplify_leads",
    url: "/leads/wtf-amplify",
    filters: buildFilters(),
  });

  // Refetch when filters or pagination changes
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        setIsPage(0); // Reset to first page when filters change
        refetch();
      },
      searchTerm || brandNameFilter ? 300 : 0 // 300ms debounce for search inputs, immediate for others
    );

    return () => clearTimeout(timeoutId);
  }, [searchTerm, brandNameFilter, startDate, endDate, refetch]);

  // Refetch when pagination changes
  useEffect(() => {
    refetch();
  }, [isPage, pageLimit, refetch]);

  const tbColumns = [
    {
      dataField: "brand_name",
      text: "Brand",
      formatter: (_, row) => (
        <div className="emeberDetailsRowContainer">
          <span className="tb-data text-start fw-semibold">
            {row?.brand_name || "NA"}
          </span>
        </div>
      ),
    },
    {
      dataField: "contact_details",
      text: "Contact Details",
      formatter: (_, row) => (
        <div className="emeberDetailsRowContainer">
          <div className="d-flex flex-column gap-1">
            {row?.contact_person_name && (
              <div className="d-flex align-items-center gap-2">
                <span className="tb-data small text-muted">
                  {row?.contact_person_name}
                </span>
              </div>
            )}
            <div className="d-flex align-items-center gap-2">
              <CallIcon color="#6c757d" />
              <small className="tb-data small">
                {row?.phone_number || "NA"}
              </small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <EmailIcon color="#6c757d" />
              <small className="tb-data small">{row?.email || "NA"}</small>
            </div>
          </div>
        </div>
      ),
    },
    {
      dataField: "web_or_insta",
      text: "Website",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.web_or_insta ? (
              <a
                href={row.web_or_insta}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-decoration-none small"
                style={{ wordBreak: "break-all" }}
              >
                {row.web_or_insta.length > 30
                  ? `${row.web_or_insta.substring(0, 30)}...`
                  : row.web_or_insta}
              </a>
            ) : (
              <span className="text-muted small">NA</span>
            )}
          </span>
        );
      },
    },
    {
      dataField: "month_ad_budget",
      text: "Budget",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            <span className="badge bg-light text-dark border">
              {row?.month_ad_budget || "NA"}
            </span>
          </span>
        );
      },
    },
    {
      dataField: "interested_in",
      text: "Interested In",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            <span className="small">{row?.interested_in || "NA"}</span>
          </span>
        );
      },
    },
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            <span className="small text-muted">
              {row?.date_added ? formatDateTime(row.date_added) : "NA"}
            </span>
          </span>
        );
      },
    },
  ];

  // Handle filter changes
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleBrandNameChange = (value) => {
    setBrandNameFilter(value);
  };

  // Handle date range change
  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setBrandNameFilter("");
    setStartDate("");
    setEndDate("");
    setIsPage(0);
  };

  // Render filters component
  const renderFilters = () => {
    return (
      <Row className="mb-4">
        <Col>
          <FilterContainer onReset={handleResetFilters}>
            {/* Brand Name Filter */}
            <div className="filter-search-wrapper">
              <input
                type="text"
                placeholder="Filter by brand name..."
                value={brandNameFilter}
                onChange={(e) => handleBrandNameChange(e.target.value)}
                className="premium-search-input"
              />
            </div>

            {/* Search Filter */}
            <div className="filter-search-wrapper">
              <input
                type="text"
                placeholder="Search by contact person name or Phone number"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="premium-search-input"
              />
            </div>

            {/* Date Range Filter */}
            <DateRangerFilter
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateRangeChange}
            />
          </FilterContainer>
        </Col>
      </Row>
    );
  };

  console.log("amplifyLeads", amplifyLeads);

  return (
    <HelmetWrapper title="WTF: Amplify Leads">
      <Container className="bg-cloud-k px-50px" fluid>
        <Row>
          <Col>
            <PageHeadingNew
              heading="Amplify Leads"
              //   subheading="Manage Amplify Leads"
            />
          </Col>
        </Row>

        {/* Filters Section */}
        {renderFilters()}

        <Row className="mt-4">
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mod-height-100vh">
              {amplifyLeadsLoading ? (
                <TableSkeleton />
              ) : (
                <PaymentLinkTable
                  tableData={amplifyLeads?.data || []}
                  tableColumns={tbColumns}
                  tablePagination={amplifyLeads?.pagination}
                  isPage={isPage}
                  setIsPage={setIsPage}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default AmplifyLanding;
